import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import PermissionManagementStore, { Permission } from '../stores/PermissionManagementStore';
import { ApplicationSdk } from '../../../core/sdk';
import './PermissionManagement.css';

interface PermissionManagementProps {
    sdk: ApplicationSdk;
}

const roleOptions = [
    { label: 'Reinigungskraft', value: 'employee' },
    { label: 'Objektleiter', value: 'manager' },
];

const PermissionEditor = observer(({ store }: { store: PermissionManagementStore }) => {
    if (!store.editor) return null;

    return (
        <Dialog
            visible={!!store.editor}
            header={store.editor.isNew ? 'Neue Berechtigung erstellen' : 'Berechtigung bearbeiten'}
            style={{ width: '450px' }}
            modal
            onHide={() => store.closeEditor()}
            footer={
                <div className="flex justify-content-end gap-2">
                    <Button
                        label="Abbrechen"
                        icon="pi pi-times"
                        outlined
                        onClick={() => store.closeEditor()}
                    />
                    <Button
                        label={store.editor.isNew ? 'Erstellen' : 'Speichern'}
                        icon="pi pi-check"
                        loading={store.loading}
                        disabled={!store.editor?.isValid}
                        onClick={() => store.savePermission()}
                    />
                </div>
            }
        >
            <div className="p-fluid">
                <div className="field">
                    <label htmlFor="name">Name der Berechtigung *</label>
                    <InputText
                        id="name"
                        value={store.editor.name}
                        onChange={e => (store.editor!.name = e.target.value)}
                        placeholder="z.B. MANAGE_ORDERS"
                        className="w-full"
                        invalid={
                            !!(
                                store.editor.name &&
                                store.editor.name.length > 0 &&
                                store.editor.name.length < 3
                            )
                        }
                    />
                    <small className="text-muted">Mindestens 3 Zeichen (z.B. MANAGE_ORDERS)</small>
                </div>

                <div className="field">
                    <label htmlFor="role">Rolle *</label>
                    <Dropdown
                        id="role"
                        value={store.editor.role}
                        options={roleOptions}
                        onChange={e => (store.editor!.role = e.value)}
                        placeholder="Rolle auswählen"
                        className="w-full"
                    />
                </div>

                <div className="field">
                    <label htmlFor="label">Label</label>
                    <InputText
                        id="label"
                        value={store.editor.label}
                        onChange={e => (store.editor!.label = e.target.value)}
                        placeholder="Benutzerfreundlicher Name"
                        className="w-full"
                    />
                    <small className="text-muted">
                        Optionaler, benutzerfreundlicher Name für die Berechtigung
                    </small>
                </div>

                <div className="field">
                    <div className="flex align-items-center">
                        <Checkbox
                            id="recommend"
                            checked={store.editor.recommend}
                            onChange={e => (store.editor!.recommend = e.checked || false)}
                        />
                        <label
                            htmlFor="recommend"
                            className="ml-2"
                        >
                            Empfohlene Berechtigung für die Rolle
                        </label>
                    </div>
                    <small className="text-muted">
                        Markiert diese Berechtigung als empfohlen für die ausgewählte Rolle
                    </small>
                </div>
            </div>
        </Dialog>
    );
});

const PermissionManagement = observer(({ sdk }: PermissionManagementProps) => {
    const [store] = useState(() => new PermissionManagementStore(sdk));

    useEffect(() => {
        store.init();
    }, [store]);

    const actionBodyTemplate = (rowData: Permission) => {
        return (
            <div className="flex gap-1">
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    size="small"
                    onClick={() => store.openEditor(rowData)}
                    tooltip="Bearbeiten"
                />
                <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    size="small"
                    severity="danger"
                    onClick={() => handleDeletePermission(rowData)}
                    tooltip="Löschen"
                />
            </div>
        );
    };

    const recommendBodyTemplate = (rowData: Permission) => {
        return rowData.recommend ? (
            <i className="pi pi-check text-green-500" />
        ) : (
            <i className="pi pi-times text-red-500" />
        );
    };

    const handleDeletePermission = (permission: Permission) => {
        confirmDialog({
            message: `Möchten Sie die Berechtigung "${permission.name}" wirklich löschen?`,
            header: 'Löschen bestätigen',
            icon: 'pi pi-exclamation-triangle',
            accept: () => store.deletePermission(permission),
            acceptLabel: 'Ja, löschen',
            rejectLabel: 'Abbrechen',
            acceptClassName: 'p-button-danger',
        });
    };

    if (store.loading && store.permissions.length === 0) {
        return (
            <div
                className="flex justify-content-center align-items-center"
                style={{ height: '200px' }}
            >
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="permission-management">
            <ConfirmDialog />
            <Card
                title="Berechtigungsverwaltung"
                subTitle="Verwalten Sie die Berechtigungen der Anwendung"
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div className="flex justify-content-between align-items-center mb-4">
                    <div className="flex align-items-center gap-3">
                        <label htmlFor="role-select">Rolle:</label>
                        <Dropdown
                            id="role-select"
                            value={store.selectedRole}
                            options={roleOptions}
                            onChange={e => store.setSelectedRole(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            style={{ minWidth: '150px' }}
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            label="Neue Berechtigung"
                            icon="pi pi-plus"
                            onClick={() => store.openEditor(null, store.selectedRole)}
                        />
                    </div>
                </div>
                <DataTable
                    value={store.filteredPermissions}
                    loading={store.loading}
                    emptyMessage="Keine Berechtigungen gefunden"
                    stripedRows
                >
                    <Column
                        field="name"
                        header="Name"
                        sortable
                    />
                    <Column
                        field="label"
                        header="Label"
                        sortable
                    />
                    <Column
                        field="recommend"
                        header="Empfohlen"
                        body={recommendBodyTemplate}
                        style={{ width: '120px', textAlign: 'center' }}
                    />
                    <Column
                        body={actionBodyTemplate}
                        header="Aktionen"
                        style={{ width: '120px', textAlign: 'center' }}
                    />
                </DataTable>
            </Card>

            <PermissionEditor store={store} />
        </div>
    );
});

export default PermissionManagement;
